import { Temporal } from "temporal-polyfill";
import type { RouterOutputs } from "@/trpc";

export type CredentialStatus =
  | "expired"
  | "expiring-soon"
  | "missing"
  | "current";

export type Filter =
  | "all"
  | "expired"
  | "expiring-soon"
  | "missing"
  | "current";

export type CredentialRegistry =
  RouterOutputs["providers"]["getCredentialRegistry"]["registry"];

const WARNING_DAYS = 30;

export const getTodayInTimezone = (timezoneId = "America/New_York"): Date =>
  new Date(
    Temporal.Now.instant()
      .toZonedDateTimeISO(timezoneId)
      .startOfDay()
      .toInstant().epochMilliseconds,
  );

export const getTodayDateString = (timezoneId = "America/New_York"): string => {
  const z = Temporal.Now.instant().toZonedDateTimeISO(timezoneId);
  return `${z.year}-${String(z.month).padStart(2, "0")}-${String(z.day).padStart(2, "0")}`;
};

export const getNyToday = (): Date => getTodayInTimezone();

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Pure status classification derived from `expiration_date`. NY-midnight today
 * compared via ISO date string slice to sidestep timezone drift on dates
 * stored as UTC midnight.
 */
export const classify = (
  expirationDate: string | null,
  today: Date,
  isPresent = Boolean(expirationDate),
): CredentialStatus => {
  if (!isPresent) {
    return "missing";
  }
  if (!expirationDate) {
    return "current";
  }
  const expStr = expirationDate.slice(0, 10);
  if (!ISO_DATE_RE.test(expStr)) {
    return "missing";
  }
  const todayStr = today.toISOString().slice(0, 10);
  if (expStr < todayStr) {
    return "expired";
  }
  const cutoff = new Date(today);
  cutoff.setUTCDate(cutoff.getUTCDate() + WARNING_DAYS);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  if (expStr <= cutoffStr) {
    return "expiring-soon";
  }
  return "current";
};

export type CredentialDocument = {
  id: string;
  type: string;
  status: CredentialStatus;
  expiresAt: string | null;
  fileTypeId: number | null;
  fileTypeIds: number[];
  required: boolean;
  notes?: string;
};

export type ProviderCredentialSummary = {
  id: string;
  affiliationId?: string;
  firstName: string;
  lastName: string;
  role: string;
  specialty: string;
  isRevealed: boolean;
  documents: CredentialDocument[];
  providerUpdateCount?: number;
  credentialViewId?: string | null;
};

export type ProviderIssueCounts = {
  expired: number;
  missing: number;
  expiringSoon: number;
  current: number;
};

type CredentialSummaryListOutput =
  RouterOutputs["providers"]["listCredentialSummary"];
type RawProvider = CredentialSummaryListOutput["providers"][number];
type RawCredential = RawProvider["credentials"][number];

const displayNameFor = (
  cred: RawCredential,
  registry: CredentialRegistry,
): string => {
  const base =
    registry[cred.credentialTypeCode]?.displayName ?? cred.credentialTypeCode;
  return cred.usStateCode ? `${base} (${cred.usStateCode})` : base;
};

export const mapProvider = (
  raw: RawProvider,
  registry: CredentialRegistry,
  today: Date = getNyToday(),
): ProviderCredentialSummary => {
  const credentialsByCode = new Map(
    raw.credentials.map((cred) => [cred.credentialTypeCode, cred]),
  );

  return {
    id: raw.id,
    affiliationId: raw.affiliationId,
    firstName: raw.firstName ?? "",
    lastName: raw.lastName ?? "",
    role: raw.role ?? "",
    specialty: raw.specialty ?? "",
    isRevealed: raw.isRevealed,
    documents: Object.values(registry).map((entry) => {
      const cred = credentialsByCode.get(entry.code);
      const fileTypeIds =
        entry.fileTypeIds ?? (entry.fileTypeId ? [entry.fileTypeId] : []);

      return {
        id: entry.code,
        type: cred ? displayNameFor(cred, registry) : entry.displayName,
        status: classify(cred?.expirationDate ?? null, today, cred?.isPresent),
        expiresAt: toIsoDateString(cred?.expirationDate ?? null),
        fileTypeId: entry.fileTypeId ?? fileTypeIds[0] ?? null,
        fileTypeIds,
        required: entry.required,
      };
    }),
  };
};

/**
 * Map a provider whose rows already carry a server-computed status.
 *
 * The new credential model resolves requirement per business unit, so the
 * browser cannot arrive at the same answer from a global required-code list —
 * it classifies nothing here and renders what the server decided. It also keys
 * rows by new-model codes (`state-license`), which is why labels come from the
 * catalog shipped with the response rather than from the legacy registry.
 *
 * `fileTypeIds` is empty on purpose: it exists so the *legacy* detail sheet can
 * find a provider's files by legacy type id, and a business unit reading the new
 * model opens the new sheet, which reads documents from the model itself.
 */
export const mapProviderFromNewModel = (
  raw: RawProvider,
  catalog: Array<{ code: string; label: string }>,
): ProviderCredentialSummary => {
  const labelByCode = new Map(
    catalog.map((entry) => [entry.code, entry.label]),
  );

  return {
    id: raw.id,
    affiliationId: raw.affiliationId,
    providerUpdateCount:
      "providerUpdateCount" in raw ? raw.providerUpdateCount : 0,
    credentialViewId: "credentialViewId" in raw ? raw.credentialViewId : null,
    firstName: raw.firstName ?? "",
    lastName: raw.lastName ?? "",
    role: raw.role ?? "",
    specialty: raw.specialty ?? "",
    isRevealed: raw.isRevealed,
    documents: raw.credentials.map((cred) => {
      const label =
        labelByCode.get(cred.credentialTypeCode) ?? cred.credentialTypeCode;
      return {
        id: cred.credentialTypeCode,
        type: cred.usStateCode ? `${label} (${cred.usStateCode})` : label,
        // Trusted, not recomputed — see the note above.
        status: (cred.status ?? "missing") as CredentialStatus,
        expiresAt: toIsoDateString(cred.expirationDate ?? null),
        fileTypeId: null,
        fileTypeIds: [],
        required: cred.required ?? false,
      };
    }),
  };
};

export const countIssues = (
  provider: ProviderCredentialSummary,
): ProviderIssueCounts => {
  const counts: ProviderIssueCounts = {
    expired: 0,
    missing: 0,
    expiringSoon: 0,
    current: 0,
  };
  for (const doc of provider.documents) {
    if (doc.status === "expired") {
      counts.expired += 1;
    } else if (doc.status === "missing" && doc.required) {
      counts.missing += 1;
    } else if (doc.status === "expiring-soon") {
      counts.expiringSoon += 1;
    } else if (doc.status === "current") {
      counts.current += 1;
    }
  }
  return counts;
};

const SEVERITY_RANK: Record<CredentialStatus, number> = {
  expired: 0,
  missing: 1,
  "expiring-soon": 2,
  current: 3,
};

export const worstStatus = (
  provider: ProviderCredentialSummary,
): CredentialStatus => {
  let worst: CredentialStatus = "current";
  for (const doc of provider.documents) {
    if (doc.status === "missing" && !doc.required) {
      continue;
    }
    if (SEVERITY_RANK[doc.status] < SEVERITY_RANK[worst]) {
      worst = doc.status;
    }
  }
  return worst;
};

// Skip 'current' so far-future expirations don't drown out near-term issues
// when sorting the dashboard by severity.
export const soonestRelevantExpiry = (
  provider: ProviderCredentialSummary,
): string | null => {
  let soonest: string | null = null;
  for (const doc of provider.documents) {
    if (!doc.expiresAt) {
      continue;
    }
    if (doc.status === "current") {
      continue;
    }
    if (!soonest || doc.expiresAt < soonest) {
      soonest = doc.expiresAt;
    }
  }
  return soonest;
};

const MS_PER_DAY = 86_400_000;

const toUtcMidnight = (yyyymmdd: string): number => {
  const y = Number(yyyymmdd.slice(0, 4));
  const m = Number(yyyymmdd.slice(5, 7));
  const d = Number(yyyymmdd.slice(8, 10));
  return Date.UTC(y, m - 1, d);
};

const toIsoDateString = (date: string | null): string | null => {
  if (!date) {
    return null;
  }
  const dateStr = date.slice(0, 10);
  return ISO_DATE_RE.test(dateStr) ? dateStr : null;
};

export const formatRelativeDays = (
  date: string | null,
  timezoneId = "America/New_York",
): string => {
  const dateStr = toIsoDateString(date);
  if (!dateStr) {
    return "—";
  }
  const todayStr = getTodayDateString(timezoneId);
  const days = (toUtcMidnight(dateStr) - toUtcMidnight(todayStr)) / MS_PER_DAY;
  if (days === 0) {
    return "today";
  }
  if (days > 0) {
    return `in ${days}d`;
  }
  return `${Math.abs(days)}d ago`;
};

export const formatDate = (date: string | null): string => {
  const dateStr = toIsoDateString(date);
  if (!dateStr) {
    return "—";
  }
  const [year, month, day] = dateStr.split("-");
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))));
};

export const sortBySeverity = (
  providers: ProviderCredentialSummary[],
): ProviderCredentialSummary[] => {
  return [...providers].sort((a, b) => {
    const aWorst = SEVERITY_RANK[worstStatus(a)];
    const bWorst = SEVERITY_RANK[worstStatus(b)];
    if (aWorst !== bWorst) {
      return aWorst - bWorst;
    }
    const aSoon = soonestRelevantExpiry(a) ?? "9999-12-31";
    const bSoon = soonestRelevantExpiry(b) ?? "9999-12-31";
    if (aSoon !== bSoon) {
      return aSoon.localeCompare(bSoon);
    }
    return a.lastName.localeCompare(b.lastName);
  });
};

export const computeKpis = (providers: ProviderCredentialSummary[]) => {
  let expired = 0;
  let expiringSoon = 0;
  let missing = 0;
  let current = 0;
  for (const p of providers) {
    const c = countIssues(p);
    expired += c.expired;
    expiringSoon += c.expiringSoon;
    missing += c.missing;
    current += c.current;
  }
  return { expired, expiringSoon, missing, current };
};
